import { ComponentItem } from "designSystem/types/component-item-types";
import { ICustomViewAttribute, ICustomViewFilter, ICustomViewGroup, ICustomViewReport, ICustomViewSort } from "src/c8-platform/type/customView";
import { ImageSize } from "src/c8-platform/type/image";
import { PrintSize } from "src/c8-platform/type/print";
import { TableHeight } from "src/c8-platform/type/table";
import { C8AttributeKey, cnl } from "src/c8-platform/type/value";

export const IMAGE_DIMENSION_MIN = 128;

export enum ShareViewWith {
	EVERYONE = "EVERYONE",
	ME = "ME",
	ROLES = "ROLES",
}

export enum ShareViewWithSubType {
	ALL = "ALL",
	SPECIFIC = "SPECIFIC",
}

/** Options for the Custom View Setting Store */
export interface IViewSettingOptions {
	/** description for custom view */
	prefDescription?: string;

	/** indicates whether the custom view is shared with site or roles */
	prefIsShared: boolean;

	/** name for custom view */
	prefName: string;

	/** parties (user or role(s)) associated with custom view */
	prefParty?: cnl[];
}

export interface ICustomViewOptions {
	/** attribute keys from the view */
	attributeKeys?: ICustomViewAttribute[];
	
	/** disable ad-hoc and batch filters */
	disableAdHoc?: boolean;
	
	/** hides the batch filter button */
	disableBatchFilter?: boolean;

	/** keys of attributes that are hidden from the view -- if a whole group is hidden, then it will exist in hiddenKeys and its child "keys" will be an empty array */
	hiddenKeys?: ICustomViewAttribute[];

	/** image size from the view */
	imageSize?: ImageSize;
	
	/** export names settings (Object<key: string  value>) */
	namesSettings? : Record<string, string>;

	/** indicates whether the view should be automatically scaled to the print size */
	printAutoScale?: boolean;

	/** print setting indicating to render attribute comments */
	printComments?: boolean;

	/** percent to scale all text by, between minimum font scale and 100, applied only if no auto scaling */
	printFontScale?: number;

	/** print setting indicating to render header logo on each page */
	printHeader?: boolean;

	/** orientation to use when printing */
	printOrientation?: string;

	/** paper size to use when printing */
	printPaper?: PrintSize;

	/** show the file size in view */
	showFileSizes?: boolean;

	/** version number */
	VR?: number;
}

/** additional options in custom view when saved to local storage */
export interface ICustomViewLocalStorageOptions {
	/** url of custom view */
	CV: cnl;

	/** key map for custom view */
	KEYS: Record<string, C8AttributeKey>;

	/** timestamp when view was saved to local storage */
	TS: number;

	/** version number */
	VR: number;
}

export interface ICustomViewOptionsInLocalStorage extends Omit<ICustomViewOptions, "VR">, ICustomViewLocalStorageOptions {
}

export interface ICustomViewQueryOptions extends ICustomViewOptions {
	/** set to true to store ad-hoc filters in local storage */
	filterRemember?: boolean;

	/** custom view query settings for all stores in view */
	filterSettings?: Record<string, ICustomViewFilter>;

	/** group settings for the table */
	groupSettings?: ICustomViewGroup;

	/** hide hierarchial nested calculations */
	hideNestedReports?: boolean;

	/** number of results to query at once - 0 means query for all */
	querySize?: number;

	/** custom view report settings */
	reportSettings?: ICustomViewReport[];

	/** should we a show a count of nodes at the bottom of table if suspended */
	showCountIfSuspended?: boolean;

	/** custom view sort settings */
	sortSettings?: ICustomViewSort[];

	/** indicates if view is set to use suspend mode */
	suspended?: boolean;

	/** set to true if view is suspended and requires at least one attribute filter before unsuspending and showing results */
	suspendedRequireFilter?: boolean;
}

export interface ICustomViewChart {
	/** determines what attribute we use to group x-axis values, will not group values if this attribute is an empty string */
	chartGroupX: string;

	/** determines what attribute we use to group y-axis values, will not group values if this attribute is an empty string  */
	chartGroupY: string;

	/** height of the chart */
	chartHeight: number;

	/** name of the chart */
	chartName: string;

	/** if ShowPercentageValues is true, determines showing data up to which decimal place */
	chartPercentageDecimalPlaces: string;

	/** indicates whether the chart show labels */
	chartShowLabels: boolean;

	/** show percentage values on specific charts, example: pie chart */
	chartShowPercentageValues: boolean;

	/** indicates whether the chart shows values */
	chartShowValues: boolean;

	/** sort by default, value descending or ascending order */
	chartSortBy: string;

	/** Subtitle of the chart */
	chartSubName: string;

	/** indicates the type of chart */
	chartTemplate: string;

	/** the value for X-axis */
	chartValue: string;

	/** TODO: validate that the comment accurately describe the purpose of the attribute */
	/** the value for Y-axis */
	chartValue2: string;

	/** width of the chart */
	chartWidth: number;

	/** name of the x-axis in the chart */
	chartXAxisName: string;

	/** name of the y-axis in the chart */
	chartYAxisName: string;
}

/** data structure of charts saved in memory */
export interface ICustomViewChartInMemory {
	/** determines what attribute we use to group x-axis values, will not group values if this attribute is an empty string */
	GX: string;

	/** determines what attribute we use to group y-axis values, will not group values if this attribute is an empty string  */
	GY: string;

	/** height of the chart */
	H: number;

	/** name of the chart */
	N: string;

	/** if ShowPercentageValues is true, determines showing data up to which decimal place */
	DP: string;

	/** indicates whether the chart show labels */
	SL: boolean;

	/** show percentage values on specific charts, example: pie chart */
	SP: boolean;

	/** indicates whether the chart shows values */
	SV: boolean;

	/** sort by default, value descending or ascending order */
	SB: string;

	/** Subtitle of the chart */
	S: string;

	/** indicates the type of chart */
	T: string;

	/** the value for X-axis */
	V1: string;

	/** TODO: validate that the comment accurately describe the purpose of the attribute */
	/** the value for Y-axis */
	V2: string;

	/** width of the chart */
	W: number;

	/** name of the x-axis in the chart */
	X: string;

	/** name of the y-axis in the chart */
	Y: string;
}

export interface ICustomViewTableOptions extends ICustomViewQueryOptions {
	/** indicate the height of the chart */
	chartHeight?: string;

	/** indicate if table should be hidden when chart is displayed */
	chartHideTable?: boolean;

	/** chart configuration for current view */
	chartSettings?: ICustomViewChart[];

	/** for hierarchical tables, level to expand table on load (count starts at 1, which means don't expand past main level) */
	expandLevel?: number;

	/** fixed column widths keyed by data point key */
	fixedColumnWidths?: Record<string, number>;

	/** if true, then column widths are determined automatically for printing (table-layout: auto) */
	printDynamicColumns?: boolean;

	/** if true, it should repeat the frozen column on every page of the print, otherwise, it should not repeat the columns on every page, it should just show on the first page */
	printFrozenColumnsForEachPage?: boolean;

	/** Indicates that "Use automatic table height in single table view" check box will be set as true */
	tableAutomaticHeight?: boolean;

	/** number of columns to freeze */
	tableFreezeColumns?: number;

	/** setting that controls max height of table */
	tableHeight?: TableHeight;

	/** indicates if root node of table should be shown; only valid when view configured to display root */
	tableHideRoot?: boolean;

	/** height to use when displaying images (overrides imageSize if non-zero) */
	tableImageHeight?: number | string;
}

export interface ICustomViewGalleryOptions extends ICustomViewQueryOptions {
	/** attributes width */
	galleryAttributeWidth?: number;

	/** image height */
	galleryImageHeight?: number;

	/** image label/cover image */
	galleryImageLabel?: string;

	/** image width */
	galleryImageWidth?: number;

	/** rows per page */
	galleryRowsPerPage?: number;

	/** card template */
	galleryTemplate?: string;

	/** print headers once (""), per page ("Page"), or per group ("Group") */
	groupHeaderStyle?: string;

	/** desired width of group header */
	groupHeaderWidth?: number;

	/** items in Row per Group */
	groupItemsPerRow?: string;
}

export interface ICustomViewGalleryOptionsV1 extends ICustomViewGalleryOptions {
	/** attribute to group results by horizontally in gallery views */
	columnGroupAttribute?: string;

	/** indicates if groups in gallery views are expanded by default */
	expandAllGroups?: boolean;

	/** attribute to group results by vertically in gallery views */
	rowGroupAttribute?: string;
}

export interface ICustomViewPropertyOptions extends ICustomViewOptions {
	/** indicates if image gallery should be shown */
	displayPropertyImage?: boolean;

	/** number of property columns */
	propertyColumns?: number;

	/** image height */
	propertyImageHeight?: number;

	/** image label/cover image */
	propertyImageLabel?: string;

	/** image width */
	propertyImageWidth?: number;

	/** indicates if the thumbnail label should be shown */
	propertyShowThumbnailLabel?: boolean;
}

export interface ICustomViewImageFormatOptions extends ICustomViewOptions {
    /** NativeExport: Exported Image Name Format */
    imageExportFormat?: string;
}

export type CanvasCommentsOrientationType = "" | "top" | "left";

export interface ICustomViewDataSheetTableOptions extends ICustomViewTableOptions {
	/** indicates if comment lines should be displayed when view first loaded */
	displayCommentLines?: boolean;
}

export interface ICustomViewDataSheetOptions extends ICustomViewOptions {
	/** indicates canvas position relative to comments ("", "top" or "left"); if empty, then orientation is determined from print orientation */
	canvasCommentsOrientation?: CanvasCommentsOrientationType;

	/** indicates if properties view will be repeated on each canvas page */
	canvasWithProperties?: boolean;

	/** height of properties view when combined with canvas; it will be subtracted from allowed canvas height */
	canvasWithPropertiesHeight?: number;

	/** all the available composite views */
	nestedViews?: Record<string, cnl>;
}

export interface ICustomViewDropDownOptions {
	/** paper orientation options */
	readonly paperOrientations?: ComponentItem[];

	/** paper size options */
	readonly paperSizes?: ComponentItem[];
}

export interface ICustomViewTableDropDownOptions extends ICustomViewDropDownOptions {
	/** chart height options */
	readonly chartHeights: ComponentItem[];

	/** expand level options */
	readonly expandLevels: ComponentItem[];

	/** freeze column options */
	readonly freezeColumns: ComponentItem[];

	/** image size options */
	imageSizes: ComponentItem[];

	/** result count options */
	readonly resultCounts: ComponentItem[];

	/** result count options indexes */
	readonly resultCountsIndex: ComponentItem;

	/** table height options */
	readonly tableHeights: ComponentItem[];
}

export interface IGalleryViewDropDownOptions extends ICustomViewDropDownOptions {
	/** card template options */
	readonly cardTemplates?: ComponentItem[];

	/** group header options */
	readonly groupHeaderStyleOptions: ComponentItem[];

	/** row per Group options */
	readonly groupItemsPerRowOptions: ComponentItem[];

	/** image label options */
	readonly imageLabels: ComponentItem[];

	/** image size options */
	readonly imageSizes: ComponentItem[];

	/** result count options */
	readonly resultCounts: ComponentItem[];
}

export interface IPropertyViewDropDownOptions extends ICustomViewDropDownOptions {
	/** image label options */
	readonly imageLabels: ComponentItem[];

	/** image size options */
	readonly imageSizes: ComponentItem[];

	/** max allowed height for print image */
	maxPrintImageHeight: number;

	/** max allowed width for print image */
	maxPrintImageWidth: number;

	/** property columns */
	readonly propertyColumns: ComponentItem[];
}

export interface IDataSheetViewDropDownOptions extends ICustomViewDropDownOptions {
	/** Canvas/Notes Orientation options */
	readonly canvasCommentsOrientations: ComponentItem<CanvasCommentsOrientationType>[];
}

export interface ICustomViewOption {
	/** former name of the option */
	readonly formerName?: string;

	/** name of the option */
	readonly name: string;

	/** set to true if option does not affect view preview */
	readonly omitFromPreview?: boolean;

	/** is this option personalized */
	readonly personalized?: boolean;

	/** secondary name of the option */
	readonly secondaryName?: string;

	/** is this option transient */
	readonly transient?: boolean;

	/** value of the option */
	readonly value: any;
}